import { DatePicker } from "antd";
import moment from "moment";
import React, { useContext, useMemo, useState } from "react";
import TimeInput from "../../common/TimeInput";
import { TrackerContext } from "../utils/types";

const ROW_HEIGHT = 46;

export const ManualTimeFields: React.FC = () => {
  const { day, setDay, setStartTime, setEndTime } = useContext(TrackerContext);

  const [startTimeInternal, setStartTimeInternal] = useState<string>(
    moment().format("HH:mm"),
  );
  const [endTimeInternal, setEndTimeInternal] = useState<string>(
    moment().add(30, "minutes").format("HH:mm"),
  );
  const [intervalDuration, setIntervalDuration] = useState<string>("0:30");

  const showPlusOneBadge = useMemo(
    () =>
      moment(endTimeInternal, "HH:mm").isBefore(
        moment(startTimeInternal, "HH:mm"),
      ),
    [startTimeInternal, endTimeInternal],
  );

  const recalculateInterval = (start?: string, end?: string) => {
    const [sh, sm] = (start || startTimeInternal).split(":");
    const [eh, em] = (end || endTimeInternal).split(":");
    let h = parseInt(eh) - parseInt(sh);
    let m = parseInt(em) - parseInt(sm);
    if (isNaN(m)) m = 0;
    if (isNaN(h)) h = 0;
    if (h < 0) h = 24 + h;
    if (m < 0) {
      m = 60 + m;
      h = h === 0 ? 23 : h - 1;
    }
    setIntervalDuration(`${h}:${m}`);
  };

  const handleStartBlur = (v?: string) => {
    setStartTimeInternal(v || "");
    setStartTime(v || "");
    recalculateInterval(v);
  };

  const handleEndBlur = (v?: string) => {
    setEndTimeInternal(v || "");
    setEndTime(v || "");
    recalculateInterval(undefined, v);
  };

  const handleDurationBlur = (duration?: string) => {
    const [h, m] = duration?.split(":") || ["0", "0"];
    const newEnd = moment(startTimeInternal, "HH:mm")
      .add(parseInt(h), "hours")
      .add(parseInt(m), "minutes")
      .format("HH:mm");
    setEndTimeInternal(newEnd);
    setEndTime(newEnd);
  };

  const getDisabledDate = (d: moment.Moment): boolean => {
    if (!d) return false;
    const now = moment();
    if (d.isAfter(now, "day")) return true;
    const isCurrentMonth = d.year() === now.year() && d.month() === now.month();
    if (isCurrentMonth) return false;
    const lastMonth = now.clone().subtract(1, "month");
    const isLastMonth =
      d.year() === lastMonth.year() && d.month() === lastMonth.month();
    if (isLastMonth && now.date() <= 5) return false;
    return true;
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "0 14px",
        height: ROW_HEIGHT,
      }}
    >
      <TimeInput
        style={{ width: 74, textAlign: "center" }}
        placeholder="Start"
        onBlur={handleStartBlur}
        onEnter={handleStartBlur}
        valueProps={startTimeInternal}
      />
      <span style={{ color: "#bbb", fontWeight: 500 }}>–</span>
      <div style={{ position: "relative" }}>
        <TimeInput
          style={{ width: 74, textAlign: "center" }}
          placeholder="End"
          onBlur={handleEndBlur}
          onEnter={handleEndBlur}
          valueProps={endTimeInternal}
        />
        {showPlusOneBadge && (
          <span
            style={{
              position: "absolute",
              top: -8,
              right: -4,
              background: "#ff6464",
              color: "white",
              borderRadius: "50%",
              fontSize: 9,
              padding: "1px 3px",
              zIndex: 10,
            }}
          >
            +1
          </span>
        )}
      </div>
      <DatePicker
        style={{ width: 130 }}
        value={day ? moment(day) : moment()}
        defaultValue={moment()}
        disabledDate={getDisabledDate}
        onSelect={(v) => setDay(v)}
        allowClear={false}
      />

      <TimeInput
        onBlur={handleDurationBlur}
        onChange={(v: string) => setIntervalDuration(v)}
        valueProps={intervalDuration}
        style={{ width: 70, textAlign: "center" }}
      />
    </div>
  );
};
